import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DateRange } from 'src/store';
import {
  BaseReportComponentProps,
  GroupByTransactionHistory,
  HideByTransactionHistory,
  HidingOptionsRequest,
  MdaReportTypeField,
  MdaReportTypes,
  SortByTransactionHistory,
} from '../../store/types';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface TransactionHistoryProps extends BaseReportComponentProps {
  expandTransactionHistory: ActionCreatorWithoutPayload<string>;
  setTransactionHistoryGroupByParameter: ActionCreatorWithPayload<GroupByTransactionHistory, string>;
  setTransactionHistorySortByParameter: ActionCreatorWithPayload<SortByTransactionHistory, string>;
  setTransactionHistorySelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangeTransactionHistoryParameter: ActionCreatorWithPayload<DateRange, string>;
  setTransactionHistoryHidingOptionsParameter: ActionCreatorWithPayload<HidingOptionsRequest<HideByTransactionHistory>, string>;
}

export const TransactionHistory = ({
  parameters,
  setTransactionHistorySortByParameter,
  setTransactionHistorySelectedParameter,
  setDateRangeTransactionHistoryParameter,
  setTransactionHistoryHidingOptionsParameter,
  setTransactionHistoryGroupByParameter,
  setGroupConsolidatedOption,
  expandTransactionHistory,
  createReportSortRequest,
}: TransactionHistoryProps): JSX.Element => {
  const handleClickTransactionsSelect = () => {
    setTransactionHistorySelectedParameter(!parameters.selectedReports?.transactionHistory.selected);
    createReportSortRequest(
      !parameters.selectedReports?.transactionHistory.selected,
      MdaReportTypes.TransactionHistory.id,
      MdaReportTypes.TransactionHistory.displayName
    );
  };

  const handleClickReportDateTransactions = (dates: DateRange) => {
    setDateRangeTransactionHistoryParameter(dates);
  };

  return (
    <Accordion onChange={expandTransactionHistory} expanded={parameters.selectedReports?.transactionHistory.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.transactionHistory.selected}
              onChangeHandler={handleClickTransactionsSelect}
            />
          }
          label="Transaction History"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            dateRange={{
              dateFrom: parameters?.selectedReports.transactionHistory?.fromDate,
              dateTo: parameters?.selectedReports.transactionHistory?.toDate,
              dateRangeType: parameters?.selectedReports.transactionHistory?.dateRangeType,
            }}
            setDateRange={handleClickReportDateTransactions}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1} alignItems="center">
          <Grid item flex={1}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-id">Sort by</InputLabel>
              <WO2Select
                labelId="sort-by-id"
                label="sort by"
                onChange={(e) => {
                  setTransactionHistorySortByParameter(e.target.value as SortByTransactionHistory);

                  if (e.target.value === SortByTransactionHistory.Account) {
                    if (parameters.selectedReports.transactionHistory.groupBy === GroupByTransactionHistory.Account) {
                      setTransactionHistoryHidingOptionsParameter({
                        value: true,
                        option: HideByTransactionHistory.Accounts,
                      });
                    } else {
                      setTransactionHistoryHidingOptionsParameter({
                        value: false,
                        option: HideByTransactionHistory.Accounts,
                      });
                    }
                  }
                }}
                value={parameters.selectedReports.transactionHistory.sortBy}
                defaultValue={SortByTransactionHistory.SecurityName}
              >
                <MenuItem value={SortByTransactionHistory.Account}>Account</MenuItem>
                <MenuItem value={SortByTransactionHistory.TradeDate}>Trade Date</MenuItem>
                <MenuItem value={SortByTransactionHistory.SecurityName}>Security Name</MenuItem>
                <MenuItem value={SortByTransactionHistory.TransactionType}>Transaction Type</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>
          <Grid item flex={1}>
            <FormControl fullWidth>
              <InputLabel id="group-by-id">Group by</InputLabel>
              <WO2Select
                labelId="group-by-id"
                label="group by"
                onChange={(e) => {
                  setTransactionHistoryGroupByParameter(e.target.value as GroupByTransactionHistory);
                  if (e.target.value === GroupByTransactionHistory.Account) {
                    setTransactionHistoryHidingOptionsParameter({
                      value: true,
                      option: HideByTransactionHistory.Accounts,
                    });
                  } else if (
                    e.target.value !== GroupByTransactionHistory.Account &&
                    parameters.selectedReports.transactionHistory.sortBy === SortByTransactionHistory.Account
                  ) {
                    setTransactionHistoryHidingOptionsParameter({
                      value: false,
                      option: HideByTransactionHistory.Accounts,
                    });
                  }
                }}
                value={parameters.selectedReports.transactionHistory.groupBy}
                defaultValue={GroupByTransactionHistory.Account}
              >
                <MenuItem value={GroupByTransactionHistory.Account}>Account</MenuItem>
                <MenuItem value={GroupByTransactionHistory.TradeDate}>Trade Date</MenuItem>
                <MenuItem value={GroupByTransactionHistory.SecurityName}>Security Name</MenuItem>
                <MenuItem value={GroupByTransactionHistory.TransactionType}>Transaction Type</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.excludeCash}
              onChange={(e) => {
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.Cash,
                });
                if (!e.target.checked) {
                  setTransactionHistoryHidingOptionsParameter({
                    value: false,
                    option: HideByTransactionHistory.Distribution,
                  });
                }
              }}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Exclude Cash</Typography>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.includeDistribution}
              onChange={(e) =>
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.Distribution,
                })
              }
              disabled={!parameters.selectedReports.transactionHistory.excludeCash}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Include Distribution</Typography>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.excludeAssetDescription}
              onChange={(e) =>
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.AssetDescription,
                })
              }
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Exclude Asset Description</Typography>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.excludeAccounts}
              onChange={(e) =>
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.Accounts,
                })
              }
              disabled={
                (parameters.selectedReports.transactionHistory.groupBy === GroupByTransactionHistory.Account &&
                  parameters.selectedReports.transactionHistory.sortBy === SortByTransactionHistory.Account) ||
                parameters.selectedReports.transactionHistory.groupBy === GroupByTransactionHistory.Account ||
                parameters.selectedReports.transactionHistory.sortBy === SortByTransactionHistory.Account
              }
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Exclude Accounts</Typography>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.includeBrokerage}
              onChange={(e) => {
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.Brokerage,
                });
                if (!e.target.checked) {
                  setTransactionHistoryHidingOptionsParameter({
                    value: false,
                    option: HideByTransactionHistory.OtherCharges,
                  });
                  setTransactionHistoryHidingOptionsParameter({
                    value: false,
                    option: HideByTransactionHistory.Gst,
                  });
                }
              }}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Include Brokerage</Typography>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.includeGST}
              onChange={(e) =>
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.Gst,
                })
              }
              disabled={!parameters.selectedReports.transactionHistory.includeBrokerage}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Include GST</Typography>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.transactionHistory.includeOtherCharges}
              onChange={(e) =>
                setTransactionHistoryHidingOptionsParameter({
                  value: e.target.checked,
                  option: HideByTransactionHistory.OtherCharges,
                })
              }
              disabled={!parameters.selectedReports.transactionHistory.includeBrokerage}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Include Other Charges</Typography>
          </Grid>
          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column" mt={1}>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={parameters.selectedReports.transactionHistory.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.TransactionHistory}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
